import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import type { Appointment, Customer, DualTrackRevenue, Tenant, Treatment } from '@/lib/types';
import { Card, PageHeader, Pill, Stat, cellStyle, thStyle } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { ntd } from '@/lib/format';

const RECALL_DAYS = 90;

export function AdvancedPage() {
  const nav = useNavigate();
  const now = useMemo(() => Date.now(), []);

  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const appts = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<Appointment[]>('/appointments') });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const revenue = useQuery({ queryKey: ['fin-revenue'], queryFn: () => api.get<DualTrackRevenue>('/finance/revenue') });
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<Tenant>('/tenant') });

  const apptList = appts.data ?? [];
  const custList = customers.data ?? [];
  const txList = treatments.data ?? [];

  // ① 喚回名單：最近一次預約超過 90 天（或從未預約）
  const recall = useMemo(() => {
    const lastByCust = new Map<string, number>();
    for (const a of apptList) {
      const t = new Date(a.startAt).getTime();
      if (!lastByCust.has(a.customerId) || t > (lastByCust.get(a.customerId) ?? 0)) lastByCust.set(a.customerId, t);
    }
    return custList
      .map((c) => ({ c, last: lastByCust.get(c.id) ?? 0 }))
      .filter((x) => x.last === 0 || now - x.last > RECALL_DAYS * 86400000)
      .map((x) => ({ ...x, days: x.last === 0 ? null : Math.floor((now - x.last) / 86400000) }))
      .sort((a, b) => (b.days ?? 9999) - (a.days ?? 9999))
      .slice(0, 20);
  }, [custList, apptList, now]);

  // ② 動態定價建議：依需求量（預約次數）排名，前 30% +10%、後 30% -10%
  const pricing = useMemo(() => {
    const count = new Map<string, number>();
    for (const a of apptList) count.set(a.treatmentId, (count.get(a.treatmentId) ?? 0) + 1);
    const rows = txList.map((t) => ({ t, demand: count.get(t.id) ?? 0 })).sort((a, b) => b.demand - a.demand);
    const n = rows.length;
    return rows.map((r, i) => {
      const pct = n >= 4 && i < Math.ceil(n * 0.3) && r.demand > 0 ? 10 : n >= 4 && i >= Math.floor(n * 0.7) ? -10 : 0;
      return { ...r, pct, suggested: Math.round((r.t.priceCents * (100 + pct)) / 100) };
    });
  }, [txList, apptList]);

  // ③ 加盟分潤：淨營業額 × royaltyPct
  const r = revenue.data;
  const pct = tenant.data?.royaltyPct ?? 0;
  const royalty = r ? Math.round((r.netOperationalCents * pct) / 100) : 0;

  return (
    <div>
      <PageHeader title="進階營運模組" subtitle="規則式建議（非黑箱 AI）：喚回名單 · 動態定價 · 加盟分潤,皆由真實營運資料推導" />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="待喚回顧客" value={String(recall.length)} hint={`> ${RECALL_DAYS} 天未回診`} tone="warn" />
        <Stat label="建議調價方案" value={String(pricing.filter((p) => p.pct !== 0).length)} tone="primary" />
        <Stat label="營運模式" value={tenant.data?.franchiseType === 'franchise' ? '加盟' : tenant.data?.franchiseType === 'overseas' ? '海外' : '直營'} tone="ink" />
        <Stat label="本期應付分潤" value={ntd(royalty)} hint={`淨額 × ${pct}%`} tone="wallet" />
      </div>

      <Card title="① 顧客喚回名單" icon={<Icon d={I.bell} size={14} style={{ color: 'var(--warn)' }} />} right={<button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => nav('/line')}>前往 LINE 喚回 →</button>}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={thStyle}>顧客</th><th style={thStyle}>電話</th><th style={thStyle}>分級</th><th style={{ ...thStyle, textAlign: 'right' }}>距上次回診</th></tr></thead>
          <tbody>
            {recall.map(({ c, days }) => (
              <tr key={c.id}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{c.name}</td>
                <td style={cellStyle} className="num">{c.phone}</td>
                <td style={cellStyle}>{c.tier}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{days === null ? <Pill bg="var(--danger-bg)" color="var(--danger)">從未回診</Pill> : `${days} 天`}</td>
              </tr>
            ))}
            {recall.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>無待喚回顧客 👍</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="② 動態定價建議" icon={<Icon d={I.trend} size={14} style={{ color: 'var(--primary)' }} />} right={<button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => nav('/catalog')}>前往調整方案 →</button>}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={thStyle}>方案</th><th style={{ ...thStyle, textAlign: 'right' }}>需求量</th><th style={{ ...thStyle, textAlign: 'right' }}>現價</th><th style={{ ...thStyle, textAlign: 'right' }}>建議</th><th style={{ ...thStyle, textAlign: 'right' }}>建議價</th></tr></thead>
          <tbody>
            {pricing.map(({ t, demand, pct, suggested }) => (
              <tr key={t.id}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{t.name}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{demand}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{ntd(t.priceCents)}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {pct > 0 ? <Pill bg="var(--success-bg)" color="var(--success)">↑ {pct}%</Pill> : pct < 0 ? <Pill bg="var(--warn-bg)" color="var(--warn)">↓ {Math.abs(pct)}%</Pill> : <span style={{ color: 'var(--ink-4)' }}>持平</span>}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: pct !== 0 ? 700 : 400, color: pct !== 0 ? 'var(--primary)' : 'var(--ink)' }} className="num">{ntd(suggested)}</td>
              </tr>
            ))}
            {pricing.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無方案</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="③ 加盟分潤試算" icon={<Icon d={I.globe} size={14} style={{ color: 'var(--primary)' }} />} right={<button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => nav('/settings')}>調整分潤 % →</button>}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', padding: 8 }}>
          <div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>淨營業額</div><div className="num" style={{ fontSize: 20, fontWeight: 700 }}>{r ? ntd(r.netOperationalCents) : '—'}</div></div>
          <Icon d={I.arrow} size={18} style={{ color: 'var(--ink-4)' }} />
          <div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>分潤比例</div><div className="num" style={{ fontSize: 20, fontWeight: 700 }}>{pct}%</div></div>
          <Icon d={I.arrow} size={18} style={{ color: 'var(--ink-4)' }} />
          <div><div style={{ fontSize: 11, color: 'var(--ink-3)' }}>應付總部分潤</div><div className="num" style={{ fontSize: 20, fontWeight: 700, color: 'var(--primary)' }}>{ntd(royalty)}</div></div>
        </div>
      </Card>
    </div>
  );
}
